import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Target, Activity, Camera, X, Save, Clock, Dumbbell, Award, Stethoscope, Image as ImageIcon } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function AthleteProfileModal({ isOpen, onClose, athleteId, onProfileUpdated }) {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    avatarUrl: '',
    phone: '',
    address: '',
    basicData: '',
    goals: '',
    experienceLevel: 'Intermedio',
    equipment: '',
    availability: '',
    medicalInfo: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/coach/clients/${athleteId}/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(prev => ({
          ...prev,
          name: data.name || '',
          email: data.email || '',
          avatarUrl: data.avatarUrl || '',
          phone: data.phone || '',
          address: data.address || '',
          basicData: data.basicData || '',
          goals: data.goals || '',
          experienceLevel: data.experienceLevel || 'Intermedio',
          equipment: data.equipment || '',
          availability: data.availability || '',
          medicalInfo: data.medicalInfo || ''
        }));
      }
    } catch (e) {
      console.error('Error fetching profile:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && athleteId) {
      fetchProfile();
    }
  }, [isOpen, athleteId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });
    
    try {
      const res = await fetch(`${API_URL}/coach/clients/${athleteId}/profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profile)
      });
      
      if (res.ok) {
        setMessage({ text: 'Ficha actualizada correctamente', type: 'success' });
        if (onProfileUpdated) onProfileUpdated();
        setTimeout(() => onClose(), 1500);
      } else {
        setMessage({ text: 'Error al actualizar', type: 'error' });
      }
    } catch (e) {
      setMessage({ text: 'Error de conexión', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
      animation: 'fadeInTab 0.3s ease-out'
    }}>
      <div className="glass-panel" style={{
        width: '95%', maxWidth: '850px', maxHeight: '90vh', overflowY: 'auto',
        position: 'relative', padding: '0'
      }}>
        {/* Header */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.5rem' }}>
            <User size={24} color="var(--health-cyan)" /> Ficha del Deportista
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.5rem' }}>
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>Cargando datos...</div>
        ) : (
          <form onSubmit={handleSave} style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            
            {/* Avatar & Basic Info Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '2rem', alignItems: 'start' }}>
              
              {/* Avatar Selector Section */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  width: '120px', height: '120px', borderRadius: '50%', overflow: 'hidden', 
                  border: '3px solid var(--glass-border)', background: 'rgba(255,255,255,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)', position: 'relative'
                }}>
                  {profile.avatarUrl ? (
                    <img src={profile.avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Camera size={40} color="var(--text-muted)" />
                  )}
                </div>
                
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label 
                    style={{ 
                      width: '100%', padding: '0.6rem', border: '1px solid var(--glass-border)', 
                      background: 'rgba(255,255,255,0.05)', color: 'white', borderRadius: '8px', 
                      fontSize: '0.85rem', cursor: 'pointer', display: 'flex', justifyContent: 'center', 
                      alignItems: 'center', gap: '8px', transition: 'all 0.2s', textAlign: 'center' 
                    }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  >
                    <ImageIcon size={16} /> Subir Fotografía
                    <input 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            setMessage({ text: 'La imagen debe ser menor a 2MB', type: 'error' });
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setProfile({...profile, avatarUrl: reader.result});
                            setMessage({ text: '', type: '' });
                          };
                          reader.readAsDataURL(file);
                        }
                      }} 
                    />
                  </label>
                  <button 
                    type="button" 
                    onClick={() => {
                      const url = prompt('Introduce la URL de la imagen de perfil:');
                      if (url) setProfile({...profile, avatarUrl: url});
                    }}
                    style={{ 
                      background: 'transparent', border: 'none', color: 'var(--text-muted)', 
                      fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' 
                    }}
                  >
                    O usar una URL web
                  </button>
                </div>
              </div>

              {/* Personal Details */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <label className="text-muted text-sm" style={{ display: 'block', marginBottom: '0.5rem' }}>Nombre Completo</label>
                  <input type="text" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} className="form-input" style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white' }} />
                </div>
                <div>
                  <label className="text-muted text-sm" style={{ display: 'block', marginBottom: '0.5rem' }}>Email</label>
                  <input type="email" value={profile.email} readOnly className="form-input" style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'gray' }} />
                </div>
                <div>
                  <label className="text-muted text-sm" style={{ display: 'block', marginBottom: '0.5rem' }}>Teléfono</label>
                  <input type="text" value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} className="form-input" style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white' }} />
                </div>
                <div>
                  <label className="text-muted text-sm" style={{ display: 'block', marginBottom: '0.5rem' }}>Dirección / Ciudad</label>
                  <input type="text" value={profile.address} onChange={e => setProfile({...profile, address: e.target.value})} className="form-input" style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white' }} />
                </div>
              </div>
            </div>

            {/* Coach Expert Fields Grid */}
            <h3 style={{ fontSize: '1.2rem', margin: '0', borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem', color: 'var(--text-secondary)' }}>Perfil Deportivo y Logística</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', background: 'rgba(255,255,255,0.02)', padding: '1.5rem', borderRadius: '12px' }}>
              <div>
                <label className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Award size={16} color="var(--strava-orange)" /> Nivel de Experiencia
                </label>
                <select 
                  value={profile.experienceLevel} 
                  onChange={e => setProfile({...profile, experienceLevel: e.target.value})}
                  style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white' }}
                >
                  <option value="Principiante" style={{color:'black'}}>Principiante</option>
                  <option value="Intermedio" style={{color:'black'}}>Intermedio</option>
                  <option value="Avanzado" style={{color:'black'}}>Avanzado</option>
                  <option value="Pro / Élite" style={{color:'black'}}>Pro / Élite</option>
                </select>
              </div>

              <div>
                <label className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Dumbbell size={16} color="var(--health-cyan)" /> Material Disponible
                </label>
                <input 
                  type="text" 
                  placeholder="Ej: Gym, Rodillo, Pulsómetro, Potenciómetro"
                  value={profile.equipment} 
                  onChange={e => setProfile({...profile, equipment: e.target.value})}
                  style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white' }}
                />
              </div>

              <div>
                <label className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Clock size={16} color="var(--health-green)" /> Disponibilidad (Horarios)
                </label>
                <input 
                  type="text" 
                  placeholder="Ej: L-V tardes, Fines de semana mañanas"
                  value={profile.availability} 
                  onChange={e => setProfile({...profile, availability: e.target.value})}
                  style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white' }}
                />
              </div>
            </div>

            {/* Stacked Text Areas for Basic Data, Medical and Goals */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              
              <div>
                <label className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Stethoscope size={16} color="#ef4444" /> Historial Médico y Alergias
                </label>
                <textarea 
                  placeholder="Asma, alergias, medicación actual, operaciones previas..."
                  value={profile.medicalInfo} 
                  onChange={e => setProfile({...profile, medicalInfo: e.target.value})}
                  rows={2}
                  style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', resize: 'vertical' }}
                />
              </div>

              <div>
                <label className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Activity size={16} color="var(--health-cyan)" /> Datos Básicos Biométricos y Lesiones
                </label>
                <textarea 
                  placeholder="Peso actual, altura, % grasa, lesiones recientes o crónicas..."
                  value={profile.basicData} 
                  onChange={e => setProfile({...profile, basicData: e.target.value})}
                  rows={3}
                  style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', resize: 'vertical' }}
                />
              </div>
              
              <div>
                <label className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Target size={16} color="var(--strava-orange)" /> Objetivos Principales y Fechas Clave
                </label>
                <textarea 
                  placeholder="Ej: Bajar de 3h en Maratón, 101 KM Ronda para 2027..."
                  value={profile.goals} 
                  onChange={e => setProfile({...profile, goals: e.target.value})}
                  rows={3}
                  style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', resize: 'vertical' }}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', marginTop: '1rem', paddingTop: '1.5rem', borderTop: '1px solid var(--glass-border)' }}>
              {message.text && (
                <span style={{ color: message.type === 'error' ? '#ef4444' : 'var(--health-green)', fontWeight: 'bold' }}>
                  {message.text}
                </span>
              )}
              <button type="button" onClick={onClose} style={{ padding: '0.75rem 1.5rem', background: 'transparent', color: 'white', border: '1px solid var(--glass-border)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.05)'} onMouseOut={e => e.target.style.background = 'transparent'}>
                Cancelar
              </button>
              <button type="submit" disabled={saving} style={{ padding: '0.75rem 1.5rem', background: 'var(--health-cyan)', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(0, 229, 255, 0.3)', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <Save size={18} /> {saving ? 'Guardando...' : 'Guardar Ficha'}
              </button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
}
